import React, { useState } from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Card, CardContent, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip } from '@mui/material';
import { Add, Edit, Delete, Search, ManageSearch, PeopleAlt } from '@mui/icons-material';
import {
  adminTabCardContentSx,
  adminTabCardSx,
  adminTabPageSubtitleSx,
  adminTabPageTitleSx,
  adminGradientContainedSx,
  adminDialogTitleSx,
} from '../styles/adminTabStyles';

const EMPTY_FORM = { name: '', phone: '', email: '', code: '', commissionPercentage: '', userAttributionDays: '' };

export default function AmbassadorsTab({
  ambassadors = [],
  filters = {},
  onApplyFilters,
  onCreate,
  onUpdate,
  onDelete,
  onViewRedemptions,
  redemptions = [],
  /** RedemptionDeletionAuditLog entries for this ambassador's code (soft-deleted redemption rows). */
  redemptionDeletionAudit = [],
  /** AmbassadorDeletionAuditLog — entire ambassador record removed (global list; filtered in modal by code). */
  deletionHistory = [],
}) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', id: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleteReason, setDeleteReason] = useState('');
  const [redemptionsDialog, setRedemptionsDialog] = useState({ open: false, name: '', code: '' });

  return (
    <Box>
      <Typography variant="h4" sx={adminTabPageTitleSx}>
        Ambassadors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={adminTabPageSubtitleSx}>
        Create and manage ambassador codes, filter by activity, and drill into attributed redemptions. Deleting an ambassador is audited.
      </Typography>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Where results appear</Typography>
        <Typography variant="body2" component="div">
          Matching ambassadors show in the <strong>Ambassador list</strong> table directly below the filters after you click <strong>Apply filters</strong>.
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 1 }}>
          <strong>Min / Max total redeems</strong> = keep only rows whose <strong>Total redeems</strong> count is inside this range (after date filter below is applied).
          <strong> Added from / to</strong> = only ambassadors <strong>created</strong> in this period are listed; the <strong>Total redeems</strong> number then counts only redemptions that also fall in <em>that same</em> date range.
        </Typography>
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={adminTabCardSx}>
            <CardContent sx={adminTabCardContentSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ManageSearch color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Search and filters
                </Typography>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}><TextField fullWidth label="Name search" value={localFilters.search || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, search: e.target.value }))} /></Grid>
                <Grid item xs={12} md={2}><TextField fullWidth label="Code" value={localFilters.code || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, code: e.target.value.toUpperCase() }))} /></Grid>
                <Grid item xs={12} md={1.5}><TextField fullWidth type="number" label="Min total redeems" value={localFilters.minRedeems || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, minRedeems: e.target.value }))} helperText="≥ this count" /></Grid>
                <Grid item xs={12} md={1.5}><TextField fullWidth type="number" label="Max total redeems" value={localFilters.maxRedeems || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, maxRedeems: e.target.value }))} helperText="≤ this count" /></Grid>
                <Grid item xs={12} md={1.5}><TextField fullWidth type="date" label="Date range from" InputLabelProps={{ shrink: true }} value={localFilters.dateFrom || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, dateFrom: e.target.value }))} helperText="Ambassador created + redeems window" /></Grid>
                <Grid item xs={12} md={1.5}><TextField fullWidth type="date" label="Date range to" InputLabelProps={{ shrink: true }} value={localFilters.dateTo || ''} onChange={(e) => setLocalFilters((p) => ({ ...p, dateTo: e.target.value }))} helperText="Ambassador created + redeems window" /></Grid>
                <Grid item xs={12} md={2.5} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" startIcon={<Search />} onClick={() => onApplyFilters(localFilters)} sx={adminGradientContainedSx}>
                    Apply filters
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => { setForm(EMPTY_FORM); setDialog({ open: true, mode: 'create', id: null }); }}
                    sx={{ textTransform: 'none' }}
                  >
                    Add ambassador
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={adminTabCardSx}>
            <CardContent sx={adminTabCardContentSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PeopleAlt color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ambassador list
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell><TableCell>Code</TableCell><TableCell>Status</TableCell><TableCell>Phone</TableCell><TableCell>Email</TableCell><TableCell>Commission %</TableCell><TableCell>Attribution days</TableCell><TableCell>Users referred</TableCell><TableCell>Total redeems</TableCell><TableCell>Commission earned (LE)</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(ambassadors || []).map((a) => (
                      <TableRow key={a._id}>
                        <TableCell>{a.name}</TableCell>
                        <TableCell>{a.code}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={a.isActive === false ? 'Inactive' : 'Active'}
                            color={a.isActive === false ? 'default' : 'success'}
                            variant={a.isActive === false ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>{a.phone}</TableCell>
                        <TableCell>{a.email || '-'}</TableCell>
                        <TableCell>{a.commissionPercentage != null ? `${a.commissionPercentage}%` : '-'}</TableCell>
                        <TableCell>{a.userAttributionDays != null ? a.userAttributionDays : '-'}</TableCell>
                        <TableCell>{a.usersAssigned ?? 0}</TableCell>
                        <TableCell>{a.totalRedeems ?? 0}</TableCell>
                        <TableCell>{Number(a.totalCommissionLe ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" startIcon={<Edit />} onClick={() => { setForm({ name: a.name || '', phone: a.phone || '', email: a.email || '', code: a.code || '', commissionPercentage: a.commissionPercentage ?? '', userAttributionDays: a.userAttributionDays ?? '' }); setDialog({ open: true, mode: 'edit', id: a._id }); }} sx={{ textTransform: 'none' }}>Edit</Button>
                          <Button size="small" variant="outlined" onClick={async () => {
                            await onUpdate(a._id, {
                              name: a.name || '',
                              phone: a.phone || '',
                              email: a.email || null,
                              commissionPercentage: a.commissionPercentage ?? 0,
                              userAttributionDays: a.userAttributionDays ?? 1,
                              isActive: a.isActive === false,
                            });
                          }} sx={{ textTransform: 'none' }}>
                            {a.isActive === false ? 'Activate' : 'Deactivate'}
                          </Button>
                          <Button
                            size="small"
                            onClick={async () => {
                              await onViewRedemptions(a);
                              setRedemptionsDialog({ open: true, name: a.name, code: a.code || '' });
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Redeems
                          </Button>
                          <Button size="small" color="error" startIcon={<Delete />} onClick={() => setDeleteDialog({ open: true, id: a._id })} sx={{ textTransform: 'none' }}>Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(ambassadors || []).length === 0 && <TableRow><TableCell colSpan={11} align="center">No ambassadors found</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'create', id: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={adminDialogTitleSx}>{dialog.mode === 'create' ? 'Create ambassador' : 'Edit ambassador'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth sx={{ mt: 1 }} label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <TextField fullWidth sx={{ mt: 2 }} label="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          <TextField fullWidth sx={{ mt: 2 }} label="Email (optional)" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          <TextField fullWidth required sx={{ mt: 2 }} label="Commission percentage" type="number" inputProps={{ min: 0, max: 100, step: '0.01' }} value={form.commissionPercentage} onChange={(e) => setForm((p) => ({ ...p, commissionPercentage: e.target.value }))} helperText="Required. Share of the platform commission the ambassador receives" />
          <TextField fullWidth required sx={{ mt: 2 }} label="Attribution period (days)" type="number" inputProps={{ min: 1, step: 1 }} value={form.userAttributionDays} onChange={(e) => setForm((p) => ({ ...p, userAttributionDays: e.target.value }))} helperText="Required. Extending or shortening this updates the expiry for everyone currently supporting this ambassador" />
          <TextField fullWidth sx={{ mt: 2 }} label="Code" value={form.code} disabled={dialog.mode !== 'create'} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog({ open: false, mode: 'create', id: null })} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const emailTrim = (form.email || '').trim();
              if (String(form.commissionPercentage || '').trim() === '' || String(form.userAttributionDays || '').trim() === '') {
                window.alert('Commission percentage and user attribution days are required.');
                return;
              }
              if (dialog.mode === 'create') {
                await onCreate({
                  name: (form.name || '').trim(),
                  phone: (form.phone || '').trim(),
                  code: (form.code || '').trim().toUpperCase(),
                  commissionPercentage: form.commissionPercentage,
                  userAttributionDays: form.userAttributionDays,
                  ...(emailTrim ? { email: emailTrim } : {}),
                });
                setDialog({ open: false, mode: 'create', id: null });
              } else {
                const updated = await onUpdate(dialog.id, {
                  name: (form.name || '').trim(),
                  phone: (form.phone || '').trim(),
                  email: emailTrim || null,
                  commissionPercentage: form.commissionPercentage,
                  userAttributionDays: form.userAttributionDays,
                });
                if (!updated) return;
                setDialog({ open: false, mode: 'create', id: null });
              }
            }}
            sx={adminGradientContainedSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => { setDeleteDialog({ open: false, id: null }); setDeleteReason(''); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={adminDialogTitleSx}>Delete ambassador</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline minRows={3} sx={{ mt: 1 }} label="Reason (required)" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteDialog({ open: false, id: null }); setDeleteReason(''); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!deleteReason.trim()} sx={{ textTransform: 'none' }} onClick={async () => {
            await onDelete(deleteDialog.id, deleteReason.trim());
            setDeleteDialog({ open: false, id: null });
            setDeleteReason('');
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={redemptionsDialog.open} onClose={() => setRedemptionsDialog({ open: false, name: '', code: '' })} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={adminDialogTitleSx}>Redemptions for {redemptionsDialog.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Active attributed redemptions (not deleted). If you remove a redemption from the <strong>Redemptions</strong> tab, it appears below under <strong>Attributed redemption deletions</strong>.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow><TableCell>User</TableCell><TableCell>Place</TableCell><TableCell>Offer</TableCell><TableCell>Date</TableCell><TableCell>Code</TableCell></TableRow></TableHead>
              <TableBody>
                {(redemptions || []).map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>{r.userId?.name || r.userId?._id || '-'}</TableCell>
                    <TableCell>{r.placeId?.name || r.placeId?._id || '-'}</TableCell>
                    <TableCell>{r.offerName || r.offerId || '-'}</TableCell>
                    <TableCell>{r.redeemedAt ? new Date(r.redeemedAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>{r.displayCode || '-'}</TableCell>
                  </TableRow>
                ))}
                {(redemptions || []).length === 0 && <TableRow><TableCell colSpan={5} align="center">No redemption records</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Attributed redemption deletions (audit)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Redemption rows deleted from the Redemptions tab that were credited to this ambassador code.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Deleted by</TableCell>
                    <TableCell>At</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Place</TableCell>
                    <TableCell>Offer</TableCell>
                    <TableCell>Display code</TableCell>
                    <TableCell>Ambassador code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(redemptionDeletionAudit || []).map((h) => {
                    const orig = h.originalRedemption || {};
                    return (
                      <TableRow key={h._id}>
                        <TableCell>{h.deletedBy?.name || h.deletedBy?._id || '-'}</TableCell>
                        <TableCell>{h.deletedAt ? new Date(h.deletedAt).toLocaleString() : '-'}</TableCell>
                        <TableCell>{h.reason || '-'}</TableCell>
                        <TableCell>{orig.userId?.name || orig.userId?._id || orig.userId || '-'}</TableCell>
                        <TableCell>{orig.placeId?.name || orig.placeId?._id || orig.placeId || '-'}</TableCell>
                        <TableCell>{orig.offerName || orig.offerId || '-'}</TableCell>
                        <TableCell>{orig.displayCode || '-'}</TableCell>
                        <TableCell>{orig.ambassadorCode || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                  {(redemptionDeletionAudit || []).length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center">No attributed redemption deletions for this code</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Ambassador record deletion (audit)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Times this <strong>ambassador profile</strong> (code) was removed from the system — not the same as deleting a redemption.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Deleted by</TableCell><TableCell>At</TableCell><TableCell>Reason</TableCell><TableCell>Ambassador</TableCell></TableRow></TableHead>
                <TableBody>
                  {(deletionHistory || [])
                    .filter((h) => String(h.originalAmbassador?.code || '').toUpperCase() === String(redemptionsDialog.code || '').toUpperCase())
                    .map((h) => (
                      <TableRow key={h._id}>
                        <TableCell>{h.deletedBy?.name || h.deletedBy?._id || '-'}</TableCell>
                        <TableCell>{h.deletedAt ? new Date(h.deletedAt).toLocaleString() : '-'}</TableCell>
                        <TableCell>{h.reason || '-'}</TableCell>
                        <TableCell>{h.originalAmbassador?.name || h.originalAmbassador?.code || '-'}</TableCell>
                      </TableRow>
                    ))}
                  {(deletionHistory || []).filter((h) => String(h.originalAmbassador?.code || '').toUpperCase() === String(redemptionsDialog.code || '').toUpperCase()).length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center">No deletion of this ambassador record</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setRedemptionsDialog({ open: false, name: '', code: '' })} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

